import { type ConsentChoice, readConsentChoice } from './consent';

// Google Analytics through gtag, with Consent Mode deciding what it may store.
let initialized = false;

function gtag(..._args: unknown[]) {
  // gtag pushes the `arguments` object itself (not an array); preserve that.
  window.dataLayer?.push(arguments);
}

interface GaOptions {
  /** The dashboard sends its own on each route change. */
  sendPageView?: boolean;
}

/** A no-op without a key, so every other call here is safe to make unconditionally. */
export function initGoogleAnalytics(gaKey: string | undefined, { sendPageView = true }: GaOptions = {}) {
  const key = (gaKey || '').trim();
  if (initialized || !key) return;
  initialized = true;

  window.dataLayer = window.dataLayer || [];
  window.gtag = gtag;

  // One default from the saved choice; the banner updates it.
  gtag('consent', 'default', consentState(readConsentChoice() ?? 'denied'));
  gtag('js', new Date());
  gtag('config', key, sendPageView ? {} : { send_page_view: false });

  // After load; the calls above wait in dataLayer.
  const loadTag = () => {
    const s = document.createElement('script');
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${key}`;
    document.head.appendChild(s);
  };
  if (document.readyState === 'complete') loadTag();
  else window.addEventListener('load', loadTag, { once: true });
}

export function trackEvent(name: string, params: Record<string, unknown> = {}): void {
  if (initialized) gtag('event', name, params);
}

export function isAnalyticsEnabled(): boolean {
  return initialized;
}

export function setAnalyticsConsent(choice: ConsentChoice): void {
  if (initialized) gtag('consent', 'update', consentState(choice));
}

export function trackPageView(path: string): void {
  if (initialized) gtag('event', 'page_view', { page_path: path });
}

function consentState(choice: ConsentChoice) {
  return { ad_storage: choice, ad_user_data: choice, ad_personalization: choice, analytics_storage: choice };
}
